"use client";
import { Box, Checkbox } from "@chakra-ui/react";
import { OptionProps, components } from "chakra-react-select";
import { difference, filter, includes, isArray, xor } from "lodash";
import { useMemo, useState } from "react";
import {
  SelectField,
  SelectFieldProps,
  SelectGroupOption,
  SelectOption,
} from "./SelectField";

interface SelectedValue<V> {
  label: string;
  value: V;
  is_group_label?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSelectGroupOption<V>(value: any): value is SelectGroupOption<V> {
  return value?.options && isArray(value.options);
}

export function GroupedCheckboxSelect<V>({
  value,
  options,
  onChange,
  ...props
}: Omit<SelectFieldProps<V>, "isMulti">) {
  const [selectedValue, setSelectedValue] = useState<SelectedValue<V>[]>(
    isArray(value) ? value : [],
  );

  const { fieldValues, selectedChildrenValues } = useMemo(() => {
    let selectedOptions = [...selectedValue];
    const selectGroupChildrenValues = [] as V[];

    const groupedSelectedOption = options.filter(
      (o: SelectGroupOption<V> | SelectOption<V>) => {
        if (!isSelectGroupOption(o)) return false;
        return o.options.every((so) => {
          return selectedOptions.some((sso) => sso.value === so.value);
        });
      },
    ) as SelectGroupOption<V>[];

    groupedSelectedOption.forEach((gOption: SelectGroupOption<V>) => {
      const groupValue = {
        label: gOption.label,
        value: gOption.label,
        is_group_label: true,
        options: gOption.options,
      };

      const groupChildrenValues = gOption.options.map(
        (cOption: SelectOption<V>) => cOption.value,
      );
      selectGroupChildrenValues.push(...groupChildrenValues);
      selectedOptions = [
        groupValue as unknown as SelectedValue<V>,
        ...selectedOptions.filter(
          (selectedOption) =>
            !gOption.options.some(
              (option) => option.value === selectedOption.value,
            ),
        ),
      ];
    });
    return {
      fieldValues: selectedOptions,
      selectedChildrenValues: selectGroupChildrenValues,
    };
  }, [options, selectedValue]);

  return (
    <SelectField
      {...props}
      hideSelectedOptions={false}
      isMulti={true}
      value={fieldValues}
      options={options}
      onChange={(newVal: SelectOption<V>) => {
        if (!isArray(newVal)) return;
        const nonGroupedVal = newVal.filter((v) => !v.is_group_label);
        const groupedOptions = filter(newVal, "is_group_label").flatMap(
          (v) => v.options,
        );
        const newValue = xor(groupedOptions, nonGroupedVal);
        setSelectedValue(newValue);
        onChange?.(newValue as unknown as SelectOption<V>);
      }}
      closeMenuOnSelect={false}
      styles={{
        option: (styles, state) => ({
          ...styles,
          backgroundColor: "none",
          color: state.isSelected ? "#111" : styles.color,
        }),
      }}
      components={{
        GroupHeading: ({ children, ...groupHeadingProps }) => {
          const isChecked =
            fieldValues.filter((v) => v.label === groupHeadingProps.data.label)
              .length > 0;

          const isIndeterminate = groupHeadingProps.data.options.some((gO) =>
            includes(fieldValues, gO),
          );
          return (
            <components.GroupHeading
              {...groupHeadingProps}
              onClick={(e) => {
                groupHeadingProps.onClick?.(e);

                if (isIndeterminate || isChecked) {
                  const newValue = difference(
                    selectedValue,
                    groupHeadingProps.data
                      .options as unknown as SelectedValue<V>[],
                  );
                  setSelectedValue(newValue);
                  onChange?.(newValue as unknown as SelectOption<V>);
                } else if (!isChecked) {
                  const newValue = [
                    ...selectedValue,
                    ...(groupHeadingProps.data
                      .options as unknown as SelectedValue<V>[]),
                  ];
                  setSelectedValue(newValue);
                  onChange?.(newValue as unknown as SelectOption<V>);
                }
              }}
            >
              <Checkbox
                style={{
                  textTransform: "capitalize",
                  fontWeight: "bold",
                }}
                isDisabled={false}
                isIndeterminate={isIndeterminate}
                isChecked={isChecked}
                border="ActiveBorder"
                colorScheme={props.colorScheme}
              >
                {children}
              </Checkbox>
            </components.GroupHeading>
          );
        },

        Group: ({ children, ...groupProps }) => {
          return (
            <components.Group {...groupProps}>
              <Box pl="1.25rem">{children}</Box>
            </components.Group>
          );
        },
        Option: ({ children, ...optionProps }: OptionProps) => {
          const data = optionProps.data as SelectOption<V>;
          return (
            <components.Option {...optionProps}>
              <Checkbox
                border="ActiveBorder"
                isChecked={
                  optionProps.isSelected ||
                  selectedChildrenValues.includes(data.value)
                }
                colorScheme={props.colorScheme}
              >
                {children}
              </Checkbox>
            </components.Option>
          );
        },
      }}
    />
  );
}
